import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getNotificationSettings, subscribeActiveCustomNotifications, DEFAULT_SETTINGS } from '../services/notificationService';
import { computeRetentionByClass } from '../services/studentStatsService';
import { getAllCardStatesMap, getProgressMeta } from '../services/progressService';

const NotificationContext = createContext(null);

export function useNotifications() {
  return useContext(NotificationContext);
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function getUserCreatedAtMs(userDoc) {
  if (!userDoc?.createdAt) return Date.now() - ONE_WEEK_MS;
  if (typeof userDoc.createdAt.toMillis === 'function') return userDoc.createdAt.toMillis();
  if (typeof userDoc.createdAt.seconds === 'number') return userDoc.createdAt.seconds * 1000;
  return Date.now() - ONE_WEEK_MS;
}

async function getLastSeenTs(uid) {
  const meta = await getProgressMeta(uid);
  const ls = meta.lastSeen;
  if (!ls) return null;
  if (typeof ls.toMillis === 'function') return ls.toMillis();
  if (typeof ls.seconds === 'number') return ls.seconds * 1000;
  return null;
}

async function generateTamkeenNotifications(cfg, uid, studentClasses) {
  const now = Date.now();
  const notifs = [];
  if (!cfg) return notifs;

  try {
    const [statesMap, lastSeenTs] = await Promise.all([
      getAllCardStatesMap(uid),
      getLastSeenTs(uid),
    ]);

    const retentionByClass = await computeRetentionByClass(statesMap, studentClasses);
    const stableTs = lastSeenTs || now;

    if (cfg.masteryDrop?.enabled) {
      const threshold = cfg.masteryDrop.threshold || 75;
      for (const cls of retentionByClass) {
        if (cls.retention < threshold && cls.retention >= 0) {
          const baseMsg = cfg.masteryDrop.message || DEFAULT_SETTINGS.tamkeen.masteryDrop.message;
          notifs.push({
            id: `mastery_drop_${cls.classId}`,
            message: `${cls.className}: ${baseMsg}`,
            icon: cfg.masteryDrop.icon || '💪',
            action: cfg.masteryDrop.action || 'page',
            pageUrl: `/student/journeys/class/${cls.classId}`,
            time: stableTs,
          });
        }
      }
    }

    if (cfg.inactivity?.enabled && lastSeenTs) {
      const daysSince = (now - lastSeenTs) / 86400000;
      if (daysSince >= (cfg.inactivity.days || 3)) {
        notifs.push({
          id: `inactivity_${Math.floor(daysSince)}`,
          message: (cfg.inactivity.message || DEFAULT_SETTINGS.tamkeen.inactivity.message).replace('{days}', Math.floor(daysSince)),
          icon: cfg.inactivity.icon || '🚀',
          action: cfg.inactivity.action || 'page',
          pageUrl: '/',
          time: lastSeenTs,
        });
      }
    }

    if (cfg.decliningCards?.enabled) {
      const threshold = cfg.decliningCards.threshold || 10;
      for (const cls of retentionByClass) {
        if (cls.declining >= threshold) {
          const baseMsg = cfg.decliningCards.message || DEFAULT_SETTINGS.tamkeen.decliningCards.message;
          notifs.push({
            id: `declining_cards_${cls.classId}`,
            message: `${cls.className}: ${baseMsg}`,
            icon: cfg.decliningCards.icon || '⚡',
            action: cfg.decliningCards.action || 'page',
            pageUrl: `/student/journeys/class/${cls.classId}`,
            time: stableTs,
          });
        }
      }
    }

    if (cfg.masteryAchievement?.enabled) {
      const achieveThreshold = cfg.masteryAchievement.threshold || 90;
      for (const cls of retentionByClass) {
        if (cls.retention >= achieveThreshold) {
          const baseMsg = (cfg.masteryAchievement.message || DEFAULT_SETTINGS.tamkeen.masteryAchievement.message).replace('{score}', cls.retention);
          notifs.push({
            id: `mastery_achievement_${cls.classId}`,
            message: `${cls.className}: ${baseMsg}`,
            icon: cfg.masteryAchievement.icon || '🏆',
            action: cfg.masteryAchievement.action || 'silent',
            time: stableTs,
          });
        }
      }
    }
  } catch (e) {
    console.error('Tamkeen notification generation error:', e);
  }

  return notifs;
}

export function NotificationProvider({ children }) {
  const { user, userDoc, role } = useAuth();
  const isStudent = role === 'student';
  const [localNotifs, setLocalNotifs] = useState([]);
  const [customList, setCustomList] = useState([]);
  const [readTs, setReadTs] = useState(() =>
    Number(localStorage.getItem('notif_read_ts') || '0')
  );
  const [settings, setSettings] = useState(null);
  const prevUidRef = useRef(user?.uid ?? null);
  const userDocRef = useRef(userDoc);
  useEffect(() => { userDocRef.current = userDoc; }, [userDoc]);

  const classesKey = useMemo(
    () => JSON.stringify(userDoc?.classes || []),
    [userDoc?.classes],
  );

  // Reset readTs when user switches
  useEffect(() => {
    const currentUid = user?.uid ?? null;
    if (currentUid !== prevUidRef.current) {
      prevUidRef.current = currentUid;
      const fresh = Number(localStorage.getItem('notif_read_ts') || '0');
      setReadTs(fresh);
    }
  }, [user?.uid]);

  // One-shot: settings + tamkeen + welcome notifications (students only).
  // Depends on classesKey (not full userDoc) so name/role-only changes
  // don't re-trigger expensive Firestore reads.
  useEffect(() => {
    if (!user || !isStudent) { setLocalNotifs([]); setSettings(null); return; }
    let cancelled = false;

    (async () => {
      try {
        const cfg = await getNotificationSettings().catch(() => DEFAULT_SETTINGS);
        if (cancelled) return;
        setSettings(cfg);

        const all = [];

        if (cfg.welcome?.enabled) {
          const createdMs = getUserCreatedAtMs(userDocRef.current);
          const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
          if (Date.now() - createdMs < THREE_DAYS) {
            all.push({
              id: 'welcome',
              message: cfg.welcome.message || DEFAULT_SETTINGS.welcome.message,
              icon: cfg.welcome.icon || '🎉',
              action: cfg.welcome.action || 'silent',
              time: createdMs,
            });
          }
        }

        const studentClasses = JSON.parse(classesKey);
        const tamkeenNotifs = await generateTamkeenNotifications(cfg.tamkeen, user.uid, studentClasses);
        if (cancelled) return;
        all.push(...tamkeenNotifs);
        setLocalNotifs(all);
      } catch (e) {
        console.error('NotificationContext load error:', e);
      }
    })();

    return () => { cancelled = true; };
  }, [user, classesKey, isStudent]);

  // Real-time: custom notifications via onSnapshot (students only)
  useEffect(() => {
    if (!user || !isStudent) { setCustomList([]); return; }
    return subscribeActiveCustomNotifications(setCustomList);
  }, [user, isStudent]);

  // Merge local + custom, filtered by student classes
  const notifications = useMemo(() => {
    const studentClasses = userDoc?.classes || [];
    const mapped = [];
    for (const cn of customList) {
      const targetIds = cn.classIds?.length > 0 ? cn.classIds : (cn.classId ? [cn.classId] : []);
      if (targetIds.length > 0 && !targetIds.some(cid => studentClasses.includes(cid))) continue;
      mapped.push({
        id: `custom_${cn.id}`,
        message: cn.text,
        icon: cn.icon || '📢',
        action: cn.action || 'silent',
        modalText: cn.modalText || '',
        pageUrl: cn.pageUrl || '',
        time: cn.createdAt?.seconds ? cn.createdAt.seconds * 1000 : Date.now(),
      });
    }
    return [...localNotifs, ...mapped].sort((a, b) => b.time - a.time);
  }, [localNotifs, customList, userDoc?.classes]);

  const unreadCount = notifications.filter(n => n.time > readTs).length;

  const markAllRead = useCallback(() => {
    const now = Date.now();
    localStorage.setItem('notif_read_ts', String(now));
    setReadTs(now);
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAllRead, readTs, settings }}>
      {children}
    </NotificationContext.Provider>
  );
}
